/**
 * WL-K14 — Client wrapper for the „Geschlossener Kreis" plausibility callable.
 *
 * Thin `httpsCallable` binding (same pattern as {@link ./advisorCockpitApi}).
 * ALL hard security checks live server-side
 * (`functions/src/moat/closedLoopCockpit.ts`): Loop-B-Beta enforcement +
 * owner-scoped reads happen in the callable — this wrapper only marshals
 * input/output and normalises errors. Strictly READ-ONLY (no write anywhere).
 *
 * @module api/closedLoopApi
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebaseConfig';

/** Traffic-light severity of a finding (mirror of the server `CheckAmpel`). */
export type ClosedLoopAmpel = 'green' | 'yellow' | 'red';

/** One plausibility finding (server `ClosedLoopFinding` / `CheckFinding`). */
export interface ClosedLoopFinding {
  checkId: string;
  ampel: ClosedLoopAmpel;
  code: string;
  evidence: Record<string, unknown>;
}

/** Response of `getClosedLoopFindings`. */
export interface ClosedLoopFindingsResult {
  findings: ClosedLoopFinding[];
  engineVersion: string;
}

/**
 * Loads the caller's own WL-K14 plausibility findings (Doppelzahlung/
 * Doppelbeleg/OP-Plausibilität/Skonto-Frist-Hinweis). The server enforces
 * Loop-B-Beta and scopes every read to `request.auth.uid` — no parameter is
 * accepted or needed (single-tenant, owner-only).
 *
 * @returns the findings list (empty ⇒ „keine Auffälligkeit erkannt").
 * @throws Error (normalised) with `code==='permission-denied'` when the caller
 *   is not Loop-B-beta, or on unexpected server failures.
 */
export async function getClosedLoopFindings(): Promise<ClosedLoopFindingsResult> {
  const callable = httpsCallable<Record<string, never>, ClosedLoopFindingsResult>(
    functions,
    'getClosedLoopFindings'
  );
  try {
    const res = await callable({});
    return res.data;
  } catch (err) {
    throw normalizeCallableError(err);
  }
}

/**
 * Maps a Firebase callable error to a plain Error, preserving the server
 * message and exposing the coarse error code without leaking internals.
 *
 * @param err the raw callable rejection.
 * @returns a plain Error carrying an optional `code`.
 */
function normalizeCallableError(err: unknown): Error & { code?: string } {
  const anyErr = err as { code?: string; message?: string };
  const message =
    typeof anyErr?.message === 'string' && anyErr.message.length > 0
      ? anyErr.message
      : 'Request failed.';
  const out = new Error(message) as Error & { code?: string };
  if (typeof anyErr?.code === 'string') {
    out.code = anyErr.code.replace(/^functions\//, '');
  }
  return out;
}
